
import React from 'react';
import {Helmet} from "react-helmet-async";

const AboutUsPage = () => {
    const features = [
        {
            icon: '🌲',
            title: 'Атмосфера та естетика',
            description: 'Стильний інтер’єр, панорамні вікна з краєвидом на карпатські схили та простора тераса для ранкової кави.'
        },
        {
            icon: '🏊',
            title: 'Релакс та відновлення',
            description: 'Власний басейн з видом на гори, затишна лаунж-зона та барбекю для теплих вечорів із найближчими.'
        },
        {
            icon: '📍',
            title: 'Ідеальна локація',
            description: 'Розташування у смт Ясіня поруч із Буковелем та Драгобратом — баланс спокою та активного відпочинку.'
        }
    ];

    const targetAudience = [
        {
            title: 'Родини та компанії',
            desc: 'Велика вітальня, оснащена кухня та комфортні спальні для спільного відпочинку.'
        },
        {
            title: 'Перезавантаження & Workation',
            desc: 'Ідеальні умови для спокійного натхнення, роботи на віддаленій основі або цифрового детоксу.'
        },
        {
            title: 'Dog-Friendly відпочинок 🐾',
            desc: 'Ми щиро раді гостям разом із їхніми чотирилапими улюбленцями.'
        }
    ];

    return (
       <>
           <Helmet>
               <title>Про нас — Hlushman House</title>
               <meta name="title" content="Про нас" />
               <meta name="description" content="Про нашу компанію, місію, цінності" />
               <meta property="og:title" content="Про нас — Hlushman House" />
               <meta property="og:description" content="Дізнайтеся більше про нашу компанію, місію, цінності та команду експертів. Історія нашого успіху та принципи роботи." />
               <meta property="og:image" content="https://hlushman.site/img/logo.jpg" />
               <meta property="og:url" content="https:///hlushman.site/about" />
           </Helmet>

           <div className="bg-stone-50 text-stone-800 min-h-screen font-sans">

               {/* Hero Section */}
               <section className="relative bg-stone-900 text-white py-24 px-6 md:px-12 text-center overflow-hidden">
                   <div className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&q=80')" }}></div>
                   <div className="relative max-w-4xl mx-auto z-10">
                       <span className="text-emerald-400 font-semibold tracking-widest uppercase text-sm">Hlushman House</span>
                       <h1 className="text-4xl md:text-6xl font-serif font-bold mt-2 mb-6 leading-tight">
                           Ваш приватний простір затишку та натхнення в серці Карпат
                       </h1>
                       <p className="text-lg md:text-xl text-stone-200 max-w-2xl mx-auto font-light">
                           Затишний вілла-простір у селищі Ясіня, створений для тих, хто шукає гармонію з природою та бажає відновити сили.
                       </p>
                   </div>
               </section>

               {/* Intro Section */}
               <section className="py-16 px-6 max-w-5xl mx-auto text-center">
                   <h2 className="text-2xl md:text-3xl font-serif font-semibold mb-6 text-stone-900">Більше, ніж просто відпочинок у горах</h2>
                   <p className="text-stone-600 leading-relaxed text-lg max-w-3xl mx-auto">
                       У <strong>Hlushman House</strong> ми поєднали сучасний комфорт та елегантний дизайн із величними панорами Карпат, кришталевим повітрям і тишею, про яку ви так довго мріяли. Це місце, де створюються спогади.
                   </p>
               </section>

               {/* Features Grid */}
               <section className="py-12 px-6 bg-white border-y border-stone-200">
                   <div className="max-w-6xl mx-auto">
                       <h2 className="text-3xl font-serif text-center font-bold mb-12 text-stone-900">Чому обирають Hlushman House</h2>
                       <div className="grid md:grid-cols-3 gap-8">
                           {features.map((item, idx) => (
                               <div key={idx} className="p-8 bg-stone-50 rounded-2xl border border-stone-100 hover:shadow-lg transition duration-300">
                                   <div className="text-4xl mb-4">{item.icon}</div>
                                   <h3 className="text-xl font-semibold mb-3 text-stone-900">{item.title}</h3>
                                   <p className="text-stone-600 leading-relaxed">{item.description}</p>
                               </div>
                           ))}
                       </div>
                   </div>
               </section>

               {/* Target Audience */}
               <section className="py-16 px-6 max-w-6xl mx-auto">
                   <h2 className="text-3xl font-serif text-center font-bold mb-12 text-stone-900">Для кого наш будинок</h2>
                   <div className="grid md:grid-cols-3 gap-6">
                       {targetAudience.map((card, idx) => (
                           <div key={idx} className="p-6 bg-emerald-900 text-white rounded-xl flex flex-col justify-between">
                               <div>
                                   <h3 className="text-xl font-semibold mb-3">{card.title}</h3>
                                   <p className="text-emerald-100 font-light text-sm leading-relaxed">{card.desc}</p>
                               </div>
                           </div>
                       ))}
                   </div>
               </section>

               {/* CTA Section */}
               <section className="py-20 px-6 bg-white/80 text-white text-center">
                   <div className="max-w-3xl mx-auto">
                       <blockquote className="italic text-xl md:text-2xl font-serif mb-8 text-emerald-600">
                           «Приїжджайте за спогадами, які залишаються в серці назавжди.»
                       </blockquote>
                       <p className="mb-8 text-stone-400">Відчуйте гостинність Hlushman House та забронюйте свій ідеальний відпочинок вже сьогодні.</p>
                       <div className="flex flex-col sm:flex-row justify-center gap-4">
                           <a href={'https://www.instagram.com/hlushman_house/'} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-8 rounded-full transition duration-300">
                               Забронювати будинок
                           </a>
                           <a href={'/contacts'} className="border border-stone-600 hover:bg-stone-800 text-emerald-700 font-medium py-3 px-8 rounded-full transition duration-300">
                               Зв'язатися з нами
                           </a>
                       </div>
                   </div>
               </section>

           </div>
       </>
    );
};

export default AboutUsPage;